/**
 * Client-side localization for the one-active-thing guard.
 *
 * Server functions throw `[busy:<kind>] <English fallback>`. This helper maps
 * the marker to a translated string, and strips the marker if no translation
 * is available so a raw marker never reaches the user.
 */
export type BusyKind = "h2h" | "joint" | "pair" | "group" | "solo" | "other" | "crossgroup" | "crossother" | "finalized";

const FINALIZED_DB = /settled result|already settled|result is permanent|permanent and cannot be deleted/i;

const MARKER = /^\[busy:(h2h|joint|pair|group|solo|other|crossgroup|crossother|finalized)\]\s*/;

/**
 * Generic server-message code marker: `[code:<snake_code>] English fallback`,
 * optionally with interpolation params: `[code:x:max=60] English fallback`.
 * The server never picks locale text — it emits a stable code plus an English
 * fallback, and the client maps it to `serverError.<code>`.
 */
const CODE_MARKER = /^\[code:([a-z0-9_]+)(?::([^\]]*))?\]\s*/;

function parseParams(raw: string | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!raw) return out;
  for (const pair of raw.split(",")) {
    const i = pair.indexOf("=");
    if (i > 0) out[pair.slice(0, i).trim()] = pair.slice(i + 1).trim();
  }
  return out;
}

export function localizeEngagementError(
  err: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
  fallback: string,
): string {
  const raw = err instanceof Error ? err.message : "";
  if (!raw) return fallback;
  // Defense in depth: the DB trigger/RLS lock-down on settled results raises a
  // raw Postgres message ("Settled results are permanent…"). Map any such text
  // to the localized copy so a trigger error never reaches a real user.
  if (FINALIZED_DB.test(raw)) {
    return t("engagementBusy.finalized", { defaultValue: raw });
  }
  const c = CODE_MARKER.exec(raw);
  if (c) {
    const stripped = raw.replace(CODE_MARKER, "");
    return t(`serverError.${c[1]}`, { defaultValue: stripped, ...parseParams(c[2]) });
  }
  const m = MARKER.exec(raw);
  if (!m) return raw;
  const kind = m[1] as BusyKind;
  const stripped = raw.replace(MARKER, "");
  return t(`engagementBusy.${kind}`, { defaultValue: stripped });
}

